import { isSupabaseConfigured, supabase } from "../lib/supabase";

const STORAGE_KEY = "almoune_tax_rates";

const defaultTaxRates = [
  { id: "tax-55", label: "TVA 5.5%", rate: 5.5, is_active: true },
  { id: "tax-10", label: "TVA 10%", rate: 10, is_active: true },
  { id: "tax-20", label: "TVA 20%", rate: 20, is_active: true },
];

const normalizeTaxRate = (row) => ({
  id: row.id || `tax-${Date.now()}`,
  label: row.label || "",
  rate: Number(row.rate || 0),
  is_active: row.is_active !== false,
  created_at: row.created_at || row.createdAt || "",
});

const readCache = () => {
  if (typeof window === "undefined") return defaultTaxRates;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultTaxRates));
      return defaultTaxRates;
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return defaultTaxRates;
    return parsed.map(normalizeTaxRate);
  } catch {
    return defaultTaxRates;
  }
};

const writeCache = (rates) => {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rates.map(normalizeTaxRate)));
  }
  return rates.map(normalizeTaxRate);
};

export const loadTaxRates = async () => {
  if (!isSupabaseConfigured || !supabase) return readCache();
  const { data, error } = await supabase
    .from("tax_rates")
    .select("*")
    .order("rate", { ascending: true });
  if (error || !data || data.length === 0) return readCache();
  return data.map(normalizeTaxRate);
};

export const saveTaxRate = async (rate) => {
  const payload = {
    id: rate.id || `tax-${Date.now()}`,
    label: rate.label || "",
    rate: Number(rate.rate || 0),
    is_active: rate.is_active !== false,
  };
  if (!isSupabaseConfigured || !supabase) {
    const all = readCache();
    const idx = all.findIndex((r) => r.id === payload.id);
    if (idx >= 0) all[idx] = { ...all[idx], ...payload };
    else all.push(payload);
    return writeCache(all);
  }
  const { error } = await supabase.from("tax_rates").upsert(payload, { onConflict: "id" });
  if (error) {
    const all = readCache();
    const idx = all.findIndex((r) => r.id === payload.id);
    if (idx >= 0) all[idx] = { ...all[idx], ...payload };
    else all.push(payload);
    return writeCache(all);
  }
  return loadTaxRates();
};

export const deleteTaxRate = async (id) => {
  if (!isSupabaseConfigured || !supabase) {
    const all = readCache().filter((r) => r.id !== id);
    return writeCache(all);
  }
  const { error } = await supabase.from("tax_rates").delete().eq("id", id);
  if (error) {
    const all = readCache().filter((r) => r.id !== id);
    return writeCache(all);
  }
  return loadTaxRates();
};

export const getDefaultTaxRate = () => defaultTaxRates[0];

export const getTaxRateById = (rates, id) => rates.find((r) => r.id === id) || null;

export const computeTTC = (ht, rate) => {
  const r = Number(rate || 0);
  return Number((Number(ht || 0) * (1 + r / 100)).toFixed(2));
};

export const computeTaxAmount = (ht, rate) => {
  const r = Number(rate || 0);
  return Number((Number(ht || 0) * r / 100).toFixed(2));
};

export const computeMargin = (sellingHT, purchaseHT) => {
  const purchase = Number(purchaseHT || 0);
  if (purchase <= 0) return null;
  return Number((((Number(sellingHT || 0) - purchase) / purchase) * 100).toFixed(1));
};
